// src/utils/date.ts
function formatDate(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(locale);
}
function formatDateTime(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString(locale);
}
function formatTime(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}
function getTimeOfDayGreeting(hour) {
  const h = hour ?? (/* @__PURE__ */ new Date()).getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
}
function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// src/utils/timeoutSignal.ts
function timeoutSignal(ms) {
  const native = AbortSignal.timeout;
  if (typeof native === "function") return native.call(AbortSignal, ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

// src/utils/format.ts
function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
function truncateText(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// src/utils/tts.ts
function resolveLang(explicit) {
  if (explicit) return explicit;
  if (typeof document !== "undefined") {
    const htmlLang = document.documentElement?.lang;
    if (htmlLang) return htmlLang;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return "en-US";
}
function speak(text, opts = {}) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      reject(new Error("SPEECH_SYNTHESIS_UNAVAILABLE"));
      return;
    }
    let utterance;
    try {
      window.speechSynthesis.cancel();
      utterance = new SpeechSynthesisUtterance(text);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("SPEECH_SYNTHESIS_CONSTRUCT_FAILED"));
      return;
    }
    utterance.lang = resolveLang(opts.lang);
    utterance.rate = opts.rate ?? 0.9;
    utterance.volume = opts.volume ?? 1;
    utterance.pitch = opts.pitch ?? 1;
    try {
      const voices = window.speechSynthesis.getVoices?.();
      if (voices && voices.length > 0) {
        const prefix = utterance.lang.split("-")[0]?.toLowerCase();
        const hasMatch = voices.some((v) => v.lang?.toLowerCase().startsWith(prefix ?? ""));
        if (!hasMatch) {
          console.warn(
            `[caro/utils/tts] No voice installed for lang "${utterance.lang}"; browser will pick a default voice.`
          );
        }
      }
    } catch {
    }
    utterance.onend = () => resolve();
    utterance.onerror = (event) => {
      const errCode = event?.error ?? "SPEECH_SYNTHESIS_ERROR";
      reject(new Error(`SPEECH_SYNTHESIS_ERROR:${errCode}`));
    };
    try {
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("SPEECH_SYNTHESIS_SPEAK_FAILED"));
    }
  });
}
var cachedAudioContext = null;
function getAudioContextCtor() {
  if (typeof window === "undefined") return null;
  const w = window;
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}
function getAudioContext() {
  if (cachedAudioContext && cachedAudioContext.state !== "closed") {
    return cachedAudioContext;
  }
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  try {
    cachedAudioContext = new Ctor();
    return cachedAudioContext;
  } catch {
    cachedAudioContext = null;
    return null;
  }
}
function beep(opts = {}) {
  return new Promise((resolve, reject) => {
    const ctx = getAudioContext();
    if (!ctx) {
      reject(new Error("AUDIO_CONTEXT_UNAVAILABLE"));
      return;
    }
    const frequency = opts.frequency ?? 880;
    const durationMs = opts.durationMs ?? 200;
    const peakVolume = opts.volume ?? 0.4;
    const attackSec = 0.02;
    const releaseSec = 0.05;
    const durationSec = durationMs / 1e3;
    const sustainSec = Math.max(durationSec - attackSec - releaseSec, 0.01);
    const totalSec = attackSec + sustainSec + releaseSec;
    let oscillator;
    let gain;
    try {
      oscillator = ctx.createOscillator();
      gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peakVolume, now + attackSec);
      gain.gain.setValueAtTime(peakVolume, now + attackSec + sustainSec);
      gain.gain.linearRampToValueAtTime(0, now + totalSec);
      oscillator.start(now);
      oscillator.stop(now + totalSec);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("AUDIO_BEEP_FAILED"));
      return;
    }
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      try {
        oscillator.disconnect();
        gain.disconnect();
      } catch {
      }
      resolve();
    };
    oscillator.onended = settle;
    setTimeout(settle, totalSec * 1e3 + 100);
  });
}
async function speakOrBeep(text, opts) {
  try {
    await speak(text, opts);
    return "spoken";
  } catch {
    try {
      await beep();
      return "beeped";
    } catch {
      return "failed";
    }
  }
}
function __resetAudioContextForTests() {
  if (cachedAudioContext) {
    try {
      void cachedAudioContext.close();
    } catch {
    }
  }
  cachedAudioContext = null;
}

// src/utils/locationData.ts
var HOISTED_KEYS = {
  phoneNumber: "phoneNumber",
  r_sip_ext_tablet: "phoneNumber",
  emergencyIntegrationIdentifier: "emergencyIntegrationIdentifier",
  r_umo: "emergencyIntegrationIdentifier"
};
var FIELD_KEY_RE = /^\d+$/;
function isLocationDataField(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value) && typeof value.name === "string";
}
function parseLocationData(data) {
  let parsed = data;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  return parsed;
}
function locationDataShape(data) {
  const parsed = parseLocationData(data);
  if (parsed == null) return "none";
  return Array.isArray(parsed) ? "array" : "object";
}
function listLocationDataFields(data) {
  const parsed = parseLocationData(data);
  if (!parsed) return [];
  if (Array.isArray(parsed)) {
    return parsed.map((entry, i) => ({ key: String(i), entry })).filter((f) => isLocationDataField(f.entry));
  }
  return Object.entries(parsed).filter(([key, value]) => FIELD_KEY_RE.test(key) && isLocationDataField(value)).map(([key, entry]) => ({ key, entry }));
}
function getLocationDataFields(data) {
  return listLocationDataFields(data).map((f) => f.entry);
}
function readLocationDataField(data, name) {
  const parsed = parseLocationData(data);
  if (!parsed) return null;
  const hit = listLocationDataFields(parsed).find((f) => f.entry.name === name);
  if (hit) return hit.entry.value === void 0 ? null : hit.entry.value;
  if (!Array.isArray(parsed) && HOISTED_KEYS[name]) {
    const hoisted = parsed[HOISTED_KEYS[name]];
    return hoisted != null && String(hoisted) !== "" ? hoisted : null;
  }
  return null;
}
function readLocationDataString(data, name) {
  const value = readLocationDataField(data, name);
  return value != null && String(value) !== "" ? String(value) : null;
}
function withLocationDataField(data, entry) {
  const parsed = parseLocationData(data);
  const { name } = entry;
  if (parsed == null || Array.isArray(parsed)) {
    const next = Array.isArray(parsed) ? [...parsed] : [];
    const idx = next.findIndex((e) => isLocationDataField(e) && e.name === name);
    if (idx >= 0) next[idx] = { ...next[idx], ...entry };
    else next.push({ ...entry });
    return next;
  }
  const out = { ...parsed };
  if (HOISTED_KEYS[name]) {
    out[HOISTED_KEYS[name]] = entry.value == null ? "" : String(entry.value);
    for (const f of listLocationDataFields(out)) if (f.entry.name === name) delete out[f.key];
    return out;
  }
  const fields = listLocationDataFields(out);
  const hit = fields.find((f) => f.entry.name === name);
  if (hit) {
    out[hit.key] = { ...out[hit.key], ...entry };
  } else {
    const max = fields.reduce((m, f) => Math.max(m, Number(f.key)), -1);
    out[String(max + 1)] = { ...entry };
  }
  return out;
}
function withoutLocationDataField(data, name) {
  const parsed = parseLocationData(data);
  if (parsed == null) return [];
  if (Array.isArray(parsed)) {
    return parsed.filter((e) => !(isLocationDataField(e) && e.name === name));
  }
  const out = { ...parsed };
  for (const f of listLocationDataFields(out)) if (f.entry.name === name) delete out[f.key];
  if (HOISTED_KEYS[name]) delete out[HOISTED_KEYS[name]];
  return out;
}
function replaceLocationDataFields(data, fields) {
  const entries = (Array.isArray(fields) ? fields : []).filter(isLocationDataField);
  const parsed = parseLocationData(data);
  if (parsed == null || Array.isArray(parsed)) return entries;
  const out = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (!(FIELD_KEY_RE.test(key) && isLocationDataField(value))) out[key] = value;
  }
  let i = 0;
  for (const entry of entries) {
    if (HOISTED_KEYS[entry.name]) {
      out[HOISTED_KEYS[entry.name]] = entry.value == null ? "" : String(entry.value);
      continue;
    }
    out[String(i++)] = entry;
  }
  return out;
}
var SITE_CUSTOM_FIELDS = {
  SITE_MANAGER_EMAIL: "site_manager_email",
  SITE_MANAGER_USER_ID: "site_manager_user_id",
  // Repair-estimates / invoicing custom fields (20260702-repair-estimates-catalog).
  // Read via `@caro/shared/utils/invoicing` helpers, mirroring getSiteManagerEmail.
  INVOICING_EMAIL: "invoicing_mail",
  // invoicing party's email (LD-INVOICE-MAIL)
  CLIENT_TAX_PROFILE: "tax_profile",
  // client tax-profile code (LD-TAX, CR-8)
  SERVICE_CONTRACT: "service_contract",
  // boolean flag: site under a service contract; gates Flow B (CR-12)
  // Per-site approval window in DAYS (2026-07-29). The 72h default is too short
  // for a client whose approval needs a board decision (a VME/syndicus meets
  // monthly); unset falls back to ESTIMATE_APPROVAL_DEFAULT_DAYS.
  ESTIMATE_APPROVAL_DAYS: "estimate_approval_days"
};
var ESTIMATE_APPROVAL_DEFAULT_DAYS = 3;
var ESTIMATE_APPROVAL_MIN_DAYS = 1;
var ESTIMATE_APPROVAL_MAX_DAYS = 90;

// src/utils/siteManager.ts
var LEGACY_RESPONSIBLE_EMAIL_FIELD = "responsibleEmail";
function getSiteManagerEmail(location) {
  const data = location?.data;
  return readLocationDataString(data, SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL) ?? readLocationDataString(data, LEGACY_RESPONSIBLE_EMAIL_FIELD);
}
function getSiteManagerUserId(location) {
  return readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID);
}
function siteHasManager(location) {
  return Boolean(getSiteManagerEmail(location));
}
function withSiteManagerFields(data, { email, userId }) {
  let next = withoutLocationDataField(data, SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL);
  next = withoutLocationDataField(next, SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID);
  if (!email) return next;
  next = withLocationDataField(next, {
    name: SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL,
    value: email,
    type: "text",
    visible: true,
    permissionLevel: "authenticated"
  });
  return withLocationDataField(next, {
    name: SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID,
    value: String(userId ?? ""),
    type: "text",
    visible: false,
    permissionLevel: "admin"
  });
}

// src/utils/invoicing.ts
function getInvoicingEmail(location) {
  return readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.INVOICING_EMAIL);
}
function getClientTaxProfile(location) {
  return readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.CLIENT_TAX_PROFILE);
}
function isServiceContractSite(location) {
  const raw = readLocationDataField(location?.data, SITE_CUSTOM_FIELDS.SERVICE_CONTRACT);
  if (raw == null) return false;
  if (typeof raw === "boolean") return raw;
  const norm = String(raw ?? "").trim().toLowerCase();
  return norm === "true" || norm === "1" || norm === "yes" || norm === "on";
}
function normalizeApprovalDays(value) {
  const n = typeof value === "number" ? value : parseInt(String(value ?? "").trim(), 10);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < ESTIMATE_APPROVAL_MIN_DAYS || rounded > ESTIMATE_APPROVAL_MAX_DAYS) return null;
  return rounded;
}
function getEstimateApprovalDays(location) {
  const raw = readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.ESTIMATE_APPROVAL_DAYS);
  return raw ? normalizeApprovalDays(raw) : null;
}
function resolveApprovalDays(location, override) {
  return normalizeApprovalDays(override) ?? getEstimateApprovalDays(location) ?? ESTIMATE_APPROVAL_DEFAULT_DAYS;
}
var INVOICING_FIELD_NAMES = [
  SITE_CUSTOM_FIELDS.INVOICING_EMAIL,
  SITE_CUSTOM_FIELDS.CLIENT_TAX_PROFILE,
  SITE_CUSTOM_FIELDS.SERVICE_CONTRACT,
  SITE_CUSTOM_FIELDS.ESTIMATE_APPROVAL_DAYS
];
function normalizeSiteCustomData(data) {
  return getLocationDataFields(data);
}
function withInvoicingFields(data, {
  invoicingEmail,
  taxProfile,
  serviceContract,
  approvalDays
}) {
  let next = withoutLocationDataField(
    data,
    INVOICING_FIELD_NAMES[0]
  );
  for (const name of INVOICING_FIELD_NAMES.slice(1)) next = withoutLocationDataField(next, name);
  const field = (name, value) => ({
    name,
    value,
    type: "text",
    visible: true,
    permissionLevel: "admin"
  });
  const email = typeof invoicingEmail === "string" ? invoicingEmail.trim() : "";
  if (email) next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.INVOICING_EMAIL, email));
  const profile = typeof taxProfile === "string" ? taxProfile.trim() : "";
  if (profile) next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.CLIENT_TAX_PROFILE, profile));
  if (serviceContract) {
    next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.SERVICE_CONTRACT, "true"));
  }
  const days = normalizeApprovalDays(approvalDays);
  if (days !== null) {
    next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.ESTIMATE_APPROVAL_DAYS, String(days)));
  }
  return next;
}
function resolveLineTax(taxCategory, taxProfile, matrix) {
  const rows = Array.isArray(matrix) ? matrix : [];
  const match = rows.find(
    (r) => r?.tax_category === taxCategory && r?.tax_profile === taxProfile && r?.active !== false && r?.active !== 0
  );
  if (!match) {
    throw new Error(
      `resolveLineTax: no active tax_rate for (tax_category=${String(
        taxCategory
      )} \xD7 tax_profile=${String(taxProfile)}). Refusing to offer/finalize (fail-closed, NFR-5).`
    );
  }
  return {
    tax_rate_bps: match.rate_bps,
    legal_mention_key: match.legal_mention_key ?? null
  };
}
function availableTaxProfiles(matrix) {
  const rows = Array.isArray(matrix) ? matrix : [];
  const profiles = /* @__PURE__ */ new Set();
  for (const r of rows) {
    if (r?.tax_profile && r.active !== false && r.active !== 0) profiles.add(r.tax_profile);
  }
  return [...profiles].sort();
}
function computeLineTaxCents(unitPriceCents, quantity, taxRateBps) {
  return Math.round(unitPriceCents * quantity * taxRateBps / 1e4);
}

// src/utils/money.ts
function formatMoneyCents(cents, currency = "EUR") {
  if (cents === null || cents === void 0 || Number.isNaN(Number(cents))) return "\u2014";
  try {
    return new Intl.NumberFormat("nl-BE", {
      style: "currency",
      currency: currency || "EUR"
    }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || "EUR"}`;
  }
}
function eurosToCents(input) {
  if (input === null || input === void 0 || input === "") return NaN;
  const n = Number(String(input).replace(",", "."));
  if (Number.isNaN(n)) return NaN;
  return Math.round(n * 100);
}
function formatBps(bps) {
  if (bps === null || bps === void 0 || Number.isNaN(Number(bps))) return "\u2014";
  const pct = Number(bps) / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}

export { __resetAudioContextForTests, availableTaxProfiles, beep, capitalize, computeLineTaxCents, eurosToCents, formatBps, formatDate, formatDateTime, formatDuration, formatFileSize, formatMoneyCents, formatTime, getClientTaxProfile, getEstimateApprovalDays, getInvoicingEmail, getLocationDataFields, getSiteManagerEmail, getSiteManagerUserId, getTimeOfDayGreeting, isLocationDataField, isServiceContractSite, listLocationDataFields, locationDataShape, normalizeApprovalDays, normalizeSiteCustomData, parseLocationData, readLocationDataField, readLocationDataString, replaceLocationDataFields, resolveApprovalDays, resolveLineTax, siteHasManager, speak, speakOrBeep, timeoutSignal, truncateText, withInvoicingFields, withLocationDataField, withSiteManagerFields, withoutLocationDataField };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map